import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { CalendarComponent, type CalendarDay } from '@sb/ui/calendar';
import { DividerDirective } from '@sb/ui/divider';
import { IconComponent } from '@sb/ui/icon';
import { LegendComponent, LegendItem } from '@sb/ui/legend';
import { fromPlainDate, parseDate, toISODateString, type PlainDate } from '@shiftbase-com/ts-shared/utilities';
import { endOfDay, isWithinInterval, startOfDay, startOfMonth } from 'date-fns';

import { AbsenceStatus, EnhancedAbsenceModel } from '../../../../../reducers/orm/absence/absence.model';
import { AbsenteeOptionModel } from '../../../../../reducers/orm/absentee-option/absentee-option.model';
import { getAllAbsenteeOptions } from '../../../../../reducers/orm/absentee-option/absentee-option.service';
import { AbsenceYearComponent } from '../absence-year/absence-year.component';
import type { AbsenceViewMode } from '../employee-absence-overview.model';
import { AbsenceCalendarDayComponent } from './absence-calendar-day.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'absence-calendar',
  imports: [
    FormsModule,
    LegendComponent,
    TranslatePipe,
    RouterLink,
    IconComponent,
    CalendarComponent,
    DividerDirective,
    AbsenceCalendarDayComponent,
    AbsenceYearComponent,
  ],
  templateUrl: './absence-calendar.component.html',
})
export class AbsenceCalendarComponent {
  #store = inject<Store<AppState>>(Store);

  year = input.required<string>();
  view = input.required<AbsenceViewMode>();
  pendingAbsences = input<EnhancedAbsenceModel[]>([]);
  reviewedAbsences = input<EnhancedAbsenceModel[]>([]);

  // The selectable year range, mirroring the year picker. The calendar may be navigated freely
  // across these bounds; crossing a year boundary emits yearChange to reload that year's data.
  minYear = input<string>();
  maxYear = input<string>();

  yearChange = output<string>();

  // The first visible month. Seeded from the selected year (keeping the month currently in view)
  // and advanced by calendar navigation; re-seeds whenever the year input changes.
  date = linkedSignal<string, PlainDate>({
    source: this.year,
    computation: (year, previous) => {
      const base = startOfMonth(previous ? fromPlainDate(previous.value) : new Date());
      base.setFullYear(Number(year));
      return toISODateString(base);
    },
  });
  minDate = computed(() => {
    const minYear = this.minYear();
    return minYear ? toISODateString(new Date(Number(minYear), 0, 1)) : undefined;
  });
  maxDate = computed(() => {
    const maxYear = this.maxYear();
    return maxYear ? toISODateString(new Date(Number(maxYear), 11, 31)) : undefined;
  });

  #absenceTypes: Signal<AbsenteeOptionModel[]> = toSignal(this.#store.select(getAllAbsenteeOptions), {
    initialValue: [],
  });
  #filteredReviewedAbsences = computed(() =>
    this.reviewedAbsences().filter((absence) => absence.status !== AbsenceStatus.DECLINED),
  );
  legendItems = computed(() => this.#getLegendItems());

  onDateChange(date: PlainDate): void {
    this.date.set(date);

    const navigatedYear = date.slice(0, 4);
    if (navigatedYear !== this.year()) {
      this.yearChange.emit(navigatedYear);
    }
  }

  getDayAbsences(day: CalendarDay): EnhancedAbsenceModel[] {
    return this.#filteredReviewedAbsences()
      .concat(this.pendingAbsences())
      .filter((absence) => {
        const absenceStartDate = startOfDay(parseDate(absence.startdate));
        const absenceEndDate = endOfDay(parseDate(absence.enddate));
        return isWithinInterval(day.date, { start: absenceStartDate, end: absenceEndDate });
      })
      .sort((a, b) => Number(a.absenteeOption.weight) - Number(b.absenteeOption.weight));
  }

  // Determine unique absence types out of pending and reviewed absences
  #getLegendItems(): LegendItem[] {
    const pendingAbsenceTypes = this.pendingAbsences().map((absence) => absence.absentee_option_id);
    const reviewedAbsenceTypes = this.#filteredReviewedAbsences().map((absence) => absence.absentee_option_id);
    return [...new Set([...pendingAbsenceTypes, ...reviewedAbsenceTypes])].map((id) => {
      const type = this.#getAbsenceType(id);
      return { label: type.option, color: type.color };
    });
  }

  #getAbsenceType(id: string): AbsenteeOptionModel {
    return this.#absenceTypes().find((absence) => absence.id === id);
  }
}
